import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { AuthConfigurationService } from '../auth/auth-configuration.service';
export type WhatsappConnectPhase = 'tenant' | 'sign-in' | 'success' | 'error';

/**
 * WhatsApp-first linking: user opens link from WhatsApp with ?session=...
 * Stays on this screen through sign-in and a success message (no dashboard redirect).
 */
@Component({
  selector: 'app-whatsapp-connect',
  templateUrl: './whatsapp-connect.component.html',
  styleUrls: ['./whatsapp-connect.component.scss'],
})
export class WhatsappConnectComponent implements OnInit, OnDestroy {
  tenantForm!: FormGroup;
  error = false;
  errorMessage = '';
  sessionId: string | null = null;
  phase: WhatsappConnectPhase = 'tenant';
  tenantDisplayName = '';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    public authConfigService: AuthConfigurationService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.tenantForm = this.fb.group({
      tenantName: [null, [Validators.required]],
    });
    this.route.queryParamMap.subscribe((params) => {
      if (params.get('linked') === '1') {
        this.phase = 'success';
        this.tenantDisplayName = localStorage.getItem('tenantName') || '';
        return;
      }
      const s = params.get('session');
      if (s) {
        this.sessionId = s;
      }
      // Prefill from connect URL (?tenantName=) when backend already knows the workspace.
      const fromUrl = (params.get('tenantName') || '').trim();
      if (fromUrl) {
        this.tenantForm.patchValue({ tenantName: fromUrl });
      }
    });
  }

  ngOnDestroy(): void {
    // no-op; handler component is short-lived per sign-in
  }

  displayFieldCss(field: string) {
    const formField = this.tenantForm.get(field);
    return {
      'is-invalid': formField && formField.invalid && (formField.dirty || formField.touched),
    };
  }

  continueLogin(): void {
    const tenantName = this.tenantForm.value.tenantName;
    if (!tenantName) {
      this.translate.get('ERROR.NO_TENANT_NAME').subscribe((res: string) => {
        this.errorMessage = res;
        this.error = true;
        this.snackBar.open(this.errorMessage, 'Dismiss', { duration: 4000 });
      });
      return;
    }
    if (!this.sessionId) {
      this.translate.get('WHATSAPP.MISSING_SESSION').subscribe((msg) => {
        this.snackBar.open(msg, 'Dismiss', { duration: 6000 });
      });
      return;
    }
    this.authConfigService
      .setTenantConfig(tenantName)
      .then(() => {
        this.tenantDisplayName = tenantName;
        this.authConfigService.configureAmplifyAuth();
        this.phase = 'sign-in';
      })
      .catch((errorResponse) => {
        this.error = true;
        this.errorMessage =
          errorResponse?.error?.message || this.translate.instant('ERROR.UNEXPECTED');
        this.snackBar.open(this.errorMessage, 'Dismiss', { duration: 5000 });
      });
  }

  onLinkSuccess(): void {
    this.phase = 'success';
    // Drop session from URL so refresh does not retry link/confirm.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { session: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onLinkFailed(_detail: string): void {
    this.phase = 'error';
    this.translate.get('WHATSAPP.LINK_FAILED').subscribe((msg) => {
      this.errorMessage = msg;
    });
  }

  switchLanguage(lang: string): void {
    localStorage.setItem('userLang', lang);
    this.translate.use(lang);
    document.documentElement.lang = lang;
    if (lang === 'he') {
      document.documentElement.dir = 'rtl';
      localStorage.setItem('direction', 'rtl');
    } else {
      document.documentElement.dir = 'ltr';
      localStorage.setItem('direction', 'ltr');
    }
  }
}
